<template>
    <form-validation v-slot="{ bindings }">
        <template-info-modal ref="modal">
            <template #list>
                <template-info-card
                    v-for="template in availableTemplates"
                    :key="template.id.value"
                    :template="template"
                    :active="selectedTemplate?.id?.value === template.id?.value"
                    @click="() => setSelectedTemplate(template)"
                />
            </template>

            <template #visual>
                <carousel-container v-if="availableImages.length > 0">
                    <template #slides>
                        <carousel-slide v-for="image in availableImages" :key="image.id.toString()">
                            <img
                                :src="image.src"
                                alt=""
                            >
                        </carousel-slide>
                    </template>

                    <template #interaction>
                        <carousel-navigation-arrow
                            direction="left"
                            horizontal-position="left"
                            vertical-position="center"
                        />

                        <carousel-navigation-arrow
                            direction="right"
                            horizontal-position="right"
                            vertical-position="center"
                        />
                        <carousel-navigation-dots
                            horizontal-position="center"
                            vertical-position="bottom"
                        />
                    </template>
                </carousel-container>
                <img
                    v-else
                    src="https://assets.winck.nl/flow/maatwerk.jpg"
                    alt="placeholder"
                    style="object-fit: cover; width: 100%; height: 100%; object-position: center"
                />
            </template>

            <template v-if="selectedTemplate && selectedTemplateInfo" #content>
                <flex-container
                    :gap="null"
                    direction="column"
                    height="full"
                    no-wrap
                >
                    <template-info-title
                        :title="selectedTemplate.name"
                        :price="selectedTemplateInfo.price"
                        :icon="selectedTemplateInfo.icon"
                    />

                    <template v-if="isMaatwerk">
                        <template v-if="view === 'description'">
                            <flex-container height="full" :gap="null">
                                <flex-container :size="5" :padding="Size.md">
                                    <div class="description" v-html="selectedTemplateInfo.longDescription" />
                                </flex-container>

                                <flex-container :size="5" height="full">
                                    <template-info-usps :usps="selectedTemplateInfo.usps" />
                                </flex-container>
                            </flex-container>
                        </template>

                        <template v-if="view === 'configure'">
                            <flex-container :padding="Size.md">
                                <strong>Instellingen</strong>
                                <input-group v-model="form.comment" :schema="string().required()">
                                    <template #label>
                                        <input-label>
                                            Vertel ons jouw doel of idee
                                        </input-label>
                                    </template>
                                    <template #input>
                                        <text-area-input height="20vh" />
                                    </template>
                                </input-group>
                            </flex-container>
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="view === 'description'">
                            <template v-if="selectedTemplateInfo.usps.length > 0">
                                <flex-container height="full" :gap="null">
                                    <flex-container :size="5" :padding="Size.md">
                                        <div class="description" v-html="selectedTemplateInfo.longDescription" />
                                    </flex-container>

                                    <flex-container :size="5" height="full">
                                        <template-info-usps :usps="selectedTemplateInfo.usps" />
                                    </flex-container>
                                </flex-container>
                            </template>
                            <template v-else>
                                <flex-container :padding="Size.md">
                                    <div class="description" v-html="selectedTemplateInfo.longDescription" />
                                </flex-container>
                            </template>
                        </template>

                        <template v-if="view === 'configure'">
                            <flex-container :padding="Size.md">
                                <flex-container>
                                    <strong>Instellingen</strong>
                                </flex-container>

                                <div v-show="loaded" class="form">
                                    <component
                                        :is="selectedTriggerEntry?.component"
                                        v-if="selectedTriggerEntry?.component"
                                        v-model="options"
                                        @loading="loaded = false"
                                        @loaded="loaded = true"
                                    />
                                    <input-group v-model="form.brand" :schema="object().required()">
                                        <template #label>
                                            <input-label>
                                                Vanuit welk merk wil je de e-mails versturen?
                                            </input-label>
                                        </template>
                                        <template #input>
                                            <select-input
                                                :items="brandsCollection.data.value"
                                                value-key-name="id"
                                                label-key-name="name"
                                            >
                                                <template #placeholder>
                                                    Kies een merk
                                                </template>
                                            </select-input>
                                        </template>
                                    </input-group>
                                    <input-group v-model="form.teams" :schema="array().of(object()).min(1)">
                                        <template #label>
                                            <input-label>
                                                Vanuit welk team wil je de e-mails versturen?
                                            </input-label>
                                        </template>
                                        <template #input>
                                            <team-multi-select-input
                                                :teams="availableTeams"
                                                :disabled="!form.brand"
                                            />
                                        </template>
                                    </input-group>
                                    <input-group v-model="form.flowName" :schema="string().required()">
                                        <template #label>
                                            <input-label>
                                                Welke naam wil je de flow geven?
                                            </input-label>
                                        </template>
                                        <template #input>
                                            <text-input />
                                        </template>
                                    </input-group>
                                </div>

                                <flex-container
                                    v-if="!loaded"
                                    :padding="Size.xl"
                                    justify-content="center"
                                >
                                    <spinner-loader v-if="!loaded" />
                                    <text-decorator size="sm">
                                        Bezig met ophalen van gegevens
                                    </text-decorator>
                                </flex-container>
                            </flex-container>
                        </template>
                    </template>
                </flex-container>
            </template>

            <template #footer>
                <template v-if="view === 'description'">
                    <button-block without-loading @completed="nextStep">
                        Volgende
                    </button-block>
                </template>

                <template v-if="view === 'configure'">
                    <button-text @click="previousStep">
                        Vorige
                    </button-text>

                    <template v-if="isMaatwerk">
                        <button-block :disabled="!bindings.isValid.value" @loading="requestMaatwerk">
                            Offerte aanvragen
                        </button-block>
                    </template>
                    <template v-else>
                        <button-block :disabled="!bindings.isValid.value" @loading="createFlowFromTemplate">
                            Genereer de flow
                        </button-block>
                    </template>
                </template>
            </template>
        </template-info-modal>
    </form-validation>
</template>

<script setup lang="ts">
    import { ButtonBlock, ButtonState, type ButtonStateChangedEvent, ButtonText, CarouselContainer, CarouselNavigationArrow, CarouselNavigationDots, CarouselSlide, type ExposedOverlay, FlexContainer, FormValidation, InputGroup, InputLabel, SelectInput, Size, TextAreaInput, TextInput } from '@keyvue/one-ui';

    import { Brand, CreateFlowFromTemplateCommand, FlowId, FlowTemplate, MediaType, RequestCustomFlowTemplateCommand, Team, TriggerType } from '@keyvue/one-api-client';

    import useClient from '@/services/keyvue-api/composables/useClient';
    import { computed, type ComputedRef, onMounted, ref, watch } from 'vue';
    import TemplateInfoCard from '@/flow/template/components/TemplateInfoCard.vue';
    import TemplateInfoTitle from '@/flow/template/components/TemplateInfoTitle.vue';
    import TemplateInfoModal from '@/flow/template/components/TemplateInfoModal.vue';
    import maatwerkTemplate from '@/flow/template/utils/maatwerk-template';
    import TemplateInfoUsps from '@/flow/template/components/TemplateInfoUsps.vue';
    import { array, object, string } from 'yup';
    import TeamMultiSelectInput from '@/company/team/components/TeamMultiSelectInput.vue';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';

    import RealworksAgendaForm from '@/flow/template/components/trigger-settings/TriggerRealworksAgenda/TriggerForm.vue';
    import RealworksTaakForm from '@/flow/template/components/trigger-settings/TriggerRealworksTaak/TriggerForm.vue';
    import RealworksKenmerkForm from '@/flow/template/components/trigger-settings/TriggerRealworksKenmerk/TriggerForm.vue';

    const {
        client,
        getResourceCollection,
        getLazyResourceCollection,
        currentCompany,
        currentUser,
    } = useClient();

    const emit = defineEmits<{
        (e: 'flow-created', flowId: string): void;
        (e: 'flow-requested'): void;
    }>();

    const modal = ref<ExposedOverlay>();
    defineExpose({
        openOverlay: () => {
            modal.value?.openOverlay();
        },
        closeOverlay: () => {
            modal.value?.closeOverlay();
        },
    });

    const templateCollection = await getResourceCollection(FlowTemplate, {
        'active': true,
        'order[name]': 'asc',
    });

    const selectedTemplate = ref<FlowTemplate | null>(null);
    const selectedTemplateInfo = computed(() => selectedTemplate.value?.templateInfo);
    const isMaatwerk = computed(() => selectedTemplate.value?.id.value === 'maatwerk');

    const availableTemplates = computed(() => [
        maatwerkTemplate,
        ...templateCollection.data.value,
    ]);

    function setSelectedTemplate(template: FlowTemplate) {
        selectedTemplate.value = template;
    }

    const view = ref('description');
    const loaded = ref(true);

    function nextStep() {
        view.value = 'configure';
    }

    function previousStep() {
        view.value = 'description';
    }

    const brandsCollection = getLazyResourceCollection(Brand, {
        'active': true,
        'order[name]': 'asc',
    });

    const options = ref<any>({});
    const form = ref<{
        brand: Brand | null;
        teams: Team[];
        flowName: string;
        comment: string;
    }>({
        brand: null,
        teams: [],
        flowName: '',
        comment: '',
    });

    function reset() {
        selectedTemplate.value = null;
        view.value = 'description';
        loaded.value = true;

        options.value = {};
        form.value = {
            brand: null,
            teams: [],
            flowName: '',
            comment: '',
        };

        // auto-select the first template if there are any
        if (availableTemplates.value.length > 0) {
            setSelectedTemplate(availableTemplates.value[0]);
        }
    }

    const availableTeams = computed(() => {
        const selectedBrand = form.value.brand;
        if (selectedBrand === null) {
            return [];
        }

        return selectedBrand.teams
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
    }) as ComputedRef<Team[]>;

    const { notifySaving, notifySaved, notifyError } = useNotify();

    async function requestMaatwerk(event: ButtonStateChangedEvent) {
        const command = RequestCustomFlowTemplateCommand.create(
            currentUser.value.id,
            form.value.comment,
        );

        const closeNotification = notifySaving('Bezig met versturen van aanvraag');
        try {
            await client.dispatch(command);
            closeNotification();
            notifySaved(undefined, 'De aanvraag is verstuurd');
            reset();
            emit('flow-requested');
        } catch (error) {
            notifyError(error);
        } finally {
            closeNotification();
            event.button.setState(ButtonState.completed);
        }
    }

    async function createFlowFromTemplate(event: ButtonStateChangedEvent) {
        if (!selectedTemplate.value) {
            return;
        }

        if (form.value.brand === null) {
            return;
        }

        const flowId = FlowId.generate();
        const command = new CreateFlowFromTemplateCommand(
            selectedTemplate.value.id.value,
            currentCompany.value.id.value,
            flowId.value,
            form.value.flowName,
            form.value.brand.id.value,
            form.value.teams.map((team) => team.id.value),
            options.value,
            false,
        );

        const closeNotification = notifySaving('Bezig met aanmaken van de flow');
        try {
            await client.dispatch(command);
            closeNotification();
            notifySaved(undefined, 'De nieuwe flow is aangemaakt');
            reset();
            emit('flow-created', command.flowId);
        } catch (error) {
            notifyError(error);
        } finally {
            closeNotification();
            event.button.setState(ButtonState.completed);
        }
    }

    const availableImages = computed(() => {
        if (!selectedTemplateInfo.value?.media?.filter) {
            return [];
        }

        return selectedTemplateInfo.value.media
            .filter((entry) => entry.type.equals(MediaType.IMAGE))
            .sort((a, b) => a.order - b.order);
    });

    const triggerComponentMap = [
        {
            type: TriggerType.REALWORKS_AGENDA,
            component: RealworksAgendaForm,
            props: {
                agendaTypes: [],
            },
        },
        {
            type: TriggerType.REALWORKS_TAAK,
            component: RealworksTaakForm,
            props: {
                taakType: null,
            },
        },
        {
            type: TriggerType.REALWORKS_KENMERK,
            component: RealworksKenmerkForm,
            props: {
                kenmerken: [],
            },
        },
    ];

    const selectedTriggerEntry = computed(() => {
        const triggers = selectedTemplate.value?.triggers;

        if (!triggers || triggers.length === 0) {
            return null;
        }

        const triggerType = triggers[0].type as TriggerType | null;
        if (!triggerType) {
            return null;
        }

        return triggerComponentMap.find(
            (entry) => entry.type.equals(triggerType),
        );
    });

    watch(() => selectedTemplate.value, () => {
        view.value = 'description';
        form.value.brand = null;
        form.value.teams = [];
        form.value.flowName = '';
        form.value.comment = '';
        options.value = {};

        if (selectedTriggerEntry.value?.props) {
            options.value = selectedTriggerEntry.value.props;
        }

        if (selectedTemplate.value) {
            form.value.flowName = selectedTemplate.value.name;
        }
    });

    onMounted(async () => {
        // auto-select the first template if there are any
        if (availableTemplates.value.length > 0) {
            setSelectedTemplate(availableTemplates.value[0]);
        }

        brandsCollection.load();
    });
</script>

<style scoped lang="scss">
.form {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: size(md);
}

.description {
    font-size: font-size(sm);
    line-height: 1.4rem;
}
</style>
